import React, { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { noop } from 'ui-components/default-props/noop';

import { NonDefaultTermsNoticeBody } from './nondefault-terms-notice-body';
import { nonDefaultWalletSellerLabelProviderIdsSet } from '../../enums/providers';
import { FeatureFlagDeclarations } from '../../feature-flags/feature-flag-declarations';
import { useFeatureFlag } from '../../feature-flags/hooks/use-feature-flag';
import { useIsAccountEmailVerifiedSellerSetting } from '../../hooks/use-seller-setting';
import { UserPermissionService } from '../../services/user-permission-service';
import Global from '../../store/global-selectors';
import { postWalletRegistrationSuccess } from '../../store/wallet/wallet.actions';
import { useNoticeLastDismissedDate } from '../hooks/use-notice-last-dismissed-date';
import { useWallet } from '../hooks/use-wallet';
import { ConditionalFsuiContextProvider } from '../modals/wallet-registration/contexts/conditional-fsui-context';
import { WalletRegistrationErrorContextProvider } from '../modals/wallet-registration/contexts/wallet-registration-error-context';
import { WalletRegistrationFormContextProvider } from '../modals/wallet-registration/contexts/wallet-registration-form-context';
import { WalletRegistrationModalContextProvider } from '../modals/wallet-registration/contexts/wallet-registration-modal-context';
import { useEligibleRequirements } from '../modals/wallet-registration/hooks/use-eligible-requirements';

/**
 * Container component that determines eligibility and provides context for non-default terms notice
 * Shows terms acceptance UI for carriers that require non-standard terms and conditions
 * Only displays to eligible US-based users with proper permissions when feature flag is enabled
 * @returns {React.JSX.Element | null} Context-wrapped notice body or null if not eligible
 */
export const NonDefaultTermsNotice = () => {
  const dispatch = useDispatch();
  const isWalletTermsModalEnabled = useFeatureFlag(FeatureFlagDeclarations.enableWalletTermsModal);
  const isFedexNoticeModal = useFeatureFlag(FeatureFlagDeclarations.enableWalletedFedexNotification);
  const isFeatureEnabled = isWalletTermsModalEnabled && !isFedexNoticeModal;

  const { walletId } = useWallet();

  const { loading, consolidatedRequirements, consolidatedTerms, consolidatedNonDefaultTerms, walletToRegister, initialized } =
    useEligibleRequirements(walletId);
  const ready = !!walletToRegister && (!loading || initialized);
  const { newLabelProviderIds = [] } = walletToRegister ?? {};
  const onClose = useCallback(() => {
    dispatch(postWalletRegistrationSuccess());
  }, [dispatch]);

  const userRoles = useSelector(Global.selectUseruserRoles);
  const sellerHomeCountry = useSelector(Global.selectSellerHomeCountry);
  const sellerFromUS = sellerHomeCountry === 'US';
  const isEmailVerified = useIsAccountEmailVerifiedSellerSetting();
  const isExpiredAccount = useSelector(Global.selectSellerIsSubscriptionExpired);
  const sellerIsActiveAndEmailVerified = isEmailVerified && !isExpiredAccount;
  const canConfigureWallet =
    UserPermissionService.userHasAdministrationRole(userRoles) ||
    UserPermissionService.userHasAccountManagementRole(userRoles) ||
    UserPermissionService.userHasConfigurationRole(userRoles);
  const { noticeLastDismissedDate, dismissNotice } = useNoticeLastDismissedDate();
  // Track dismissal, wallet has it's own tracking for activation

  const isIncluded = newLabelProviderIds?.some((id) => nonDefaultWalletSellerLabelProviderIdsSet.has(id.toString()));

  const shouldShow = useMemo(
    () =>
      consolidatedTerms.length === 0 &&
      ready &&
      sellerIsActiveAndEmailVerified &&
      sellerFromUS &&
      !noticeLastDismissedDate &&
      canConfigureWallet &&
      isIncluded &&
      !loading &&
      isFeatureEnabled,
    [
      consolidatedTerms,
      ready,
      sellerIsActiveAndEmailVerified,
      sellerFromUS,
      noticeLastDismissedDate,
      canConfigureWallet,
      isIncluded,
      loading,
      isFeatureEnabled,
    ],
  );

  if (!shouldShow) {
    return null;
  }

  return (
    <WalletRegistrationFormContextProvider
      consolidatedTerms={consolidatedTerms}
      nonDefaultTerms={consolidatedNonDefaultTerms}
      registrationRequirements={consolidatedRequirements}
      walletToRegister={walletToRegister}
    >
      <WalletRegistrationModalContextProvider
        closeWalletModal={onClose}
        isUpdate
        labelPurchaseCallback={noop}
        nonDefaultTerms={consolidatedNonDefaultTerms}
        registrationRequirements={consolidatedRequirements}
        terms={consolidatedTerms}
        walletToRegister={walletToRegister}
      >
        <WalletRegistrationErrorContextProvider>
          <ConditionalFsuiContextProvider currencyCode={walletToRegister?.currencyCode}>
            <NonDefaultTermsNoticeBody consolidatedNonDefaultTerms={consolidatedNonDefaultTerms} dismissNotice={dismissNotice} />
          </ConditionalFsuiContextProvider>
        </WalletRegistrationErrorContextProvider>
      </WalletRegistrationModalContextProvider>
    </WalletRegistrationFormContextProvider>
  );
};
